import {
  Check,
  Close,
  CloudDownload,
  CloudUpload,
  ControlPoint,
  Delete,
  Edit,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Card,
  CardMedia,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { RoomContext } from "App/context";
import GenshinButton from "common/components/GenshinButton";
import GenshinDialog from "common/components/GenshinDialog";
import {
  NotificationContext,
  useErrorNotification,
} from "common/components/Notification";
import Paper from "common/components/Paper";
import Typography from "common/components/Typography";
import VisuallyHiddenInput from "common/components/VisuallyHiddenInput";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { API_URL } from "service/root/rootApi";
import { createSynergy, deleteSynergy, updateSynergy } from "service/sheet";
import { CharacterSynergy, Synergy } from "service/sheet/type";

type SynergiesDialogProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  sheetId: number;
  synergies: Synergy[];
  canEdit: boolean;
  fetch: () => Promise<void>;
};

const SynergyItem = (props: {
  synergy?: Synergy;
  canEdit?: boolean;
  sheetId: number;
  fetch: () => Promise<void>;
}) => {
  const { synergy, canEdit = false, sheetId, fetch } = props;
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [form, setForm] = useState<Omit<Synergy, "id">>({
    characterA: synergy?.characterA ?? {
      name: "Yoimiya",
      constellationThreshold: 0,
      value: 0,
    },
    characterB: synergy?.characterB ?? {
      name: "Yoimiya",
      constellationThreshold: 0,
      value: 0,
    },
    available: synergy?.available ?? true,
  });
  const { t } = useTranslation();
  const { charactersList } = useContext(RoomContext);
  const dispatchError = useErrorNotification();

  if (!synergy && !editMode) {
    return (
      <Card
        sx={{
          p: 1,
          bgcolor: ({ palette }) => palette.background.highlight,
          width: "250px",
          height: "210px",
        }}
      >
        <Button
          sx={{ height: "100%" }}
          onClick={() => setEditMode(true)}
          color="success"
        >
          <Stack
            border="dashed"
            borderColor="gray"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={1}
          >
            <Typography size="m" textAlign="center">
              {t("Ajouter une synergie")}
            </Typography>
            <ControlPoint style={{ height: "3rem", width: "3rem" }} />
          </Stack>
        </Button>
      </Card>
    );
  }

  const handleConfirmSynergy = async () => {
    if (!synergy) {
      try {
        await createSynergy(sheetId, form);
        await fetch();
        setEditMode(false);
      } catch (error) {
        dispatchError(error as string);
      }
    } else {
      if (
        JSON.stringify(form.characterA) !==
          JSON.stringify(synergy.characterA) ||
        JSON.stringify(form.characterB) !==
          JSON.stringify(synergy.characterB) ||
        form.available !== synergy.available
      ) {
        try {
          await updateSynergy(sheetId, synergy.id, form);
          await fetch();
          setEditMode(false);
        } catch (error) {
          dispatchError(error as string);
        }
      } else setEditMode(false);
    }
  };

  const displayCharacter = (
    character: CharacterSynergy,
    formField: keyof Pick<Synergy, "characterA" | "characterB">,
  ) => (
    <Stack direction="column" alignItems="center" flex={1} minWidth={0}>
      <Stack width="100%" alignItems="center">
        <CardMedia
          component="img"
          src={`${API_URL}assets/genshin/characters/${editMode ? form[formField].name : character.name}/thumb.png`}
          sx={{
            width: "5rem",
            height: "5rem",
            filter:
              !editMode && !synergy?.available ? "grayscale(100%)" : "none",
          }}
        />
        {editMode ? (
          <Autocomplete
            value={form[formField].name}
            onChange={(_, newName) =>
              setForm({
                ...form,
                [formField]: {
                  ...form[formField],
                  name: newName,
                },
              })
            }
            options={charactersList.map((char) => char.name)}
            size="small"
            disableClearable
            sx={{ width: "90%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                InputLabelProps={{
                  ...params.InputLabelProps,
                  children: undefined,
                }}
              />
            )}
          />
        ) : (
          <Typography
            size="sm"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            textAlign="center"
            width="100%"
            color={!synergy?.available ? "GrayText" : "inherit"}
          >
            {character.name}
          </Typography>
        )}
      </Stack>
      {editMode ? (
        <>
          <Select
            value={form[formField].constellationThreshold}
            variant="standard"
            onChange={(evt) =>
              setForm({
                ...form,
                [formField]: {
                  ...form[formField],
                  constellationThreshold: evt.target.value,
                },
              })
            }
          >
            {Array.from({ length: 7 }, (_, i) => i).map((value) => (
              <MenuItem key={value} value={value}>
                C{value}
                {value < 6 && "+"}
              </MenuItem>
            ))}
          </Select>
          <TextField
            type="number"
            variant="standard"
            sx={{ width: "5rem" }}
            value={form[formField].value}
            onChange={(e) =>
              setForm({
                ...form,
                [formField]: {
                  ...form[formField],
                  value: parseInt(e.target.value),
                },
              })
            }
          />
        </>
      ) : (
        <>
          <Typography
            size="m"
            textAlign="center"
            color={!synergy?.available ? "GrayText" : "inherit"}
          >
            C{character.constellationThreshold}
            {character.constellationThreshold < 6 && "+"}
          </Typography>
          <Typography
            size="l"
            textAlign="center"
            color={!synergy?.available ? "GrayText" : "inherit"}
          >
            {character.value}
          </Typography>
        </>
      )}
    </Stack>
  );

  const displayFooter = () => {
    if (editMode) {
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Switch
            checked={form.available}
            onChange={(evt) =>
              setForm({ ...form, available: evt.target.checked })
            }
          />
          <Stack direction="row" alignItems="center">
            <IconButton
              color="error"
              onClick={() => setEditMode(false)}
              sx={{ padding: 0.5, height: "fit-content" }}
            >
              <Close />
            </IconButton>
            <IconButton
              color="success"
              onClick={handleConfirmSynergy}
              sx={{ padding: 0.5, height: "fit-content" }}
            >
              <Check />
            </IconButton>
          </Stack>
        </Stack>
      );
    } else if (canEdit && isHovering) {
      return (
        <Stack position="absolute" width="100%" height="100%">
          <IconButton
            color="error"
            sx={{ position: "absolute", left: 5 }}
            onClick={async () => {
              if (synergy) {
                await deleteSynergy(sheetId, synergy.id);
                await fetch();
              }
            }}
          >
            <Delete />
          </IconButton>
          <IconButton
            sx={{ position: "absolute", right: 5 }}
            color="primary"
            onClick={() => setEditMode(true)}
          >
            <Edit />
          </IconButton>
        </Stack>
      );
    } else return <></>;
  };

  return (
    <Card
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      sx={{
        position: "relative",
        p: 1,
        bgcolor: ({ palette }) => palette.background.highlight,
        width: "250px",
        height: "210px",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        height="100%"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {displayCharacter(
            synergy?.characterA ?? form.characterA,
            "characterA",
          )}
          {displayCharacter(
            synergy?.characterB ?? form.characterB,
            "characterB",
          )}
        </Stack>
        {displayFooter()}
      </Stack>
    </Card>
  );
};

const SynergiesDialog = (props: SynergiesDialogProps) => {
  const { open, setOpen, synergies, canEdit, sheetId, fetch } = props;
  const { t } = useTranslation();
  const { dispatchNotification } = useContext(NotificationContext);
  const dispatchError = useErrorNotification();

  const handleUploadSynergies = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const fileReader = new FileReader();
      fileReader.readAsText(event.target.files[0], "UTF-8");
      fileReader.onload = async (e) => {
        try {
          const csv = (e.target?.result as string)
            .split("\n")
            .filter((line) => line.trim() !== "");
          for (const s of csv) {
            const synergyInfo = s.split(";");
            await createSynergy(sheetId, {
              characterA: {
                name: synergyInfo[0],
                constellationThreshold: parseInt(synergyInfo[1]),
                value: parseInt(synergyInfo[2]),
              },
              characterB: {
                name: synergyInfo[3],
                constellationThreshold: parseInt(synergyInfo[4]),
                value: parseInt(synergyInfo[5]),
              },
              available: synergyInfo[6] === "true",
            });
          }
          await fetch();
          dispatchNotification({
            message: t("Les synergies ont été importées avec succès"),
            severity: "success",
          });
        } catch (error) {
          dispatchError(error as string);
        }
      };
    }
  };

  const convertDataToCSV = () => {
    let res = "";
    for (const s of synergies) {
      const data = [
        s.characterA.name,
        s.characterA.constellationThreshold,
        s.characterA.value,
        s.characterB.name,
        s.characterB.constellationThreshold,
        s.characterB.value,
        s.available,
      ];
      res += data.join(";") + "\n";
    }
    return res;
  };

  return (
    <GenshinDialog
      open={open}
      fullWidth
      maxWidth="lg"
      onClose={() => setOpen(false)}
      height="60vh"
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {t("Synergies")}
          {canEdit && (
            <Stack sx={{ ".MuiButtonBase-root": { px: 1.5 } }} direction="row">
              <Tooltip title={t("Importer")} arrow>
                <IconButton component="label">
                  <CloudUpload />
                  <VisuallyHiddenInput
                    type="file"
                    accept=".csv,application/csv"
                    onChange={(event) => handleUploadSynergies(event)}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Télécharger")} arrow>
                <IconButton
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                    convertDataToCSV(),
                  )}`}
                  download="synergies.csv"
                >
                  <CloudDownload />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={2} height="100%">
          <Paper
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 260px)",
              gap: 2,
              flexGrow: 1,
              overflow: "auto",
              height: "100%",
              overflowX: "hidden",
            }}
          >
            {canEdit && <SynergyItem sheetId={sheetId} fetch={fetch} />}
            {synergies.map((synergy) => (
              <SynergyItem
                key={synergy.id}
                synergy={synergy}
                canEdit={canEdit}
                sheetId={sheetId}
                fetch={fetch}
              />
            ))}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <GenshinButton onClick={() => setOpen(false)}>
          {t("Quitter")}
        </GenshinButton>
      </DialogActions>
    </GenshinDialog>
  );
};

export default SynergiesDialog;
